import { useCallback, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  DefaultDescriptionBenefitEducation,
  FormBase,
  FormModal,
  type IBaseModalProps,
  SelectData,
  TemplatesType,
  formatUnknownErrorMessage,
  templatesTypes,
  useToast,
} from '@gbs-monorepo-packages/common';
import {
  type InsertPageSchema,
  insertPageSchema,
} from '@gbs-monorepo/edge/formSchemas/pageSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import { VALIDATION_LIMITS } from '../../../../constants/ValidationRules';
import { useCourse } from '../../../../hooks/useCourse';
import {
  type ICoursePageDTO,
  createCoursePageFromTemplate,
} from '../../../../services/coursePages';
import { generateNegativeTimestamp } from '../../../../utils/generate';
import Logger from '../../../../utils/logger';
import { SelectTemplates } from '../SelectTemplates';
import { SelectContainer, SelectTemplatesWrapper } from './styles';

interface IAddPageModalProps extends Partial<
  Omit<IBaseModalProps, 'children' | 'onOpenChange'>
> {
  clientId: number;
  onAccept: (course: ICoursePageDTO) => void;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
}

const fromScratch = TemplatesType.BUILD_SCRATCH;

export const AddPageModal = ({
  clientId,
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  zIndex,
  ...props
}: IAddPageModalProps): JSX.Element | null => {
  const isSelectOpen = useRef(new Set());
  const [loadingTemplateContent, setLoadingTemplateContent] =
    useState<boolean>(false);
  const { addToast } = useToast();
  const { selectedCourse } = useCourse();

  const addPage = useForm<InsertPageSchema>({
    resolver: zodResolver(insertPageSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      title: '',
      templateOptionsId: fromScratch,
      templateId: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
  } = addPage;

  const values = watch();

  const onSubmit: SubmitHandler<InsertPageSchema> = useCallback(
    async (data: InsertPageSchema) => {
      setLoadingTemplateContent(true);
      try {
        const templateIdNum = data.templateId
          ? parseInt(data.templateId, 10)
          : null;

        let newPage: ICoursePageDTO;

        if (templateIdNum !== null && !isNaN(templateIdNum)) {
          newPage = await createCoursePageFromTemplate({
            courseId: selectedCourse?.id ?? 0,
            templateId: templateIdNum,
            title: data.title,
          });
        } else {
          const tempId = generateNegativeTimestamp();

          newPage = {
            components: '{}',
            createdAt: '',
            cssContent: '',
            hasWrapper: true,
            htmlContent: '',
            id: tempId,
            pageNumber: 0,
            title: data.title,
            updatedAt: '',
          };
        }

        onAccept?.(newPage);
        reset({
          title: '',
          templateOptionsId: fromScratch,
          templateId: '',
        });
      } catch (error) {
        Logger.debug('error: ', error);
        addToast({
          dataCy: 'add-page-from-template-error-toast',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          title: 'Error on get template',
        });
      } finally {
        setLoadingTemplateContent(false);
      }
    },
    [addToast, onAccept, reset, selectedCourse?.id]
  );

  const handleDeclineAddPage = useCallback(() => {
    if (!isSelectOpen.current.size) {
      onDecline?.();
      reset({
        title: '',
        templateOptionsId: fromScratch,
        templateId: '',
      });
    }
  }, [onDecline, reset]);

  const handleOnTemplateChange = useCallback(
    (id: string) => {
      setValue('templateId', id, { shouldValidate: true });
    },
    [setValue]
  );

  const handleChangeTemplateOption = useCallback(
    (templateTypeStr: string) => {
      const newTemplateOptionsId = Number(templateTypeStr);
      setValue('templateOptionsId', newTemplateOptionsId, {
        shouldValidate: true,
      });
      if (newTemplateOptionsId === (fromScratch as number)) {
        setValue('templateId', '', { shouldValidate: true });
      }
    },
    [setValue]
  );

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  return !open ? null : (
    <FormBase.Provider {...addPage}>
      <FormModal
        loading={loadingTemplateContent}
        dataCy="add-course-page-form-modal"
        {...props}
        open={!waitToOpen}
        acceptText="Create course page"
        declineText="Cancel"
        mainText="Create"
        onAccept={handleSubmit(onSubmit)}
        onDecline={handleDeclineAddPage}
        onOpenChange={handleDeclineAddPage}
        zIndex={zIndex}
        disabled={!isValid || loadingTemplateContent}
      >
        <FormBase.Content>
          <FormBase.InputContent
            dataCy="label-pageTitle"
            filled={!!values.title}
            label="Title"
            errorMessage={errors.title?.message}
          >
            <FormBase.InputText
              id="page-title"
              autoComplete="off"
              dataCy="input-pageTitle"
              name="title"
              value={values.title}
              maxLength={VALIDATION_LIMITS.COURSE.TITLE_MAX}
              required
              type="text"
            />
          </FormBase.InputContent>

          <SelectContainer data-cy="template-type">
            <FormBase.InputContent
              dataCy="label-templateType"
              filled
              inputRef="template-type"
              label="Template Type"
              value={String(values.templateOptionsId ?? fromScratch)}
              isSelectData
            >
              <SelectData
                defaultValue={String(values.templateOptionsId ?? fromScratch)}
                data={templatesTypes}
                placeholder="Choose a template type"
                onValueChange={handleChangeTemplateOption}
                onOpenChange={(isOpen) => {
                  handleOpenChange(isOpen, 'type');
                }}
                required
                zIndex={10}
              />
            </FormBase.InputContent>
          </SelectContainer>
          {values.templateOptionsId !== (fromScratch as number) && (
            <SelectContainer data-cy="template-content">
              <FormBase.InputContent
                dataCy="label-templateContent"
                filled
                inputRef="template-content"
                label="Template Content"
                value={String(values.templateId ?? '')}
                isSelectData
              >
                <SelectTemplatesWrapper>
                  <SelectTemplates
                    attribute="title"
                    clientId={clientId}
                    templateOptionsId={values.templateOptionsId ?? fromScratch}
                    disabled={loadingTemplateContent}
                    onValueChange={handleOnTemplateChange}
                    onOpenChange={(isOpen) => {
                      handleOpenChange(isOpen, 'options');
                    }}
                    required
                    zIndex={10}
                  />
                </SelectTemplatesWrapper>
              </FormBase.InputContent>
            </SelectContainer>
          )}
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
